import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { wizardBridge } from '../services/wizard-bridge';

/**
 * Kontextuelle In-Dialog-Tour fuer den Wochenmuster-Wizard
 * (`StaffingWizardComponent`). Dies ist der Dialog, der sich beim Klick auf
 * "＋ Neues Muster" / "Wochenmuster bearbeiten" / "Einzelnen Termin
 * bearbeiten" tatsaechlich oeffnet. Gestartet ueber den
 * Hilfe-Button (`<app-dialog-help-button>`) rechts im Wizard-Header.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * MODUS-ABHAENGIG (2026-06-09): Derselbe Wizard erscheint in DREI fachlich
 * unterschiedlichen Modi (`wizardBridge.getMode()`):
 *
 *   • `create`           — Neues Wochenmuster anlegen. Du definierst eine
 *                          wiederkehrende REGEL; daraus erzeugt das System die
 *                          unbesetzten Schichten. Preset-Schnellstart sichtbar.
 *   • `edit-template`    — Bestehendes Muster (Regel) bearbeiten. Aenderungen
 *                          wirken auf ALLE kuenftigen Termine: erzeugte
 *                          Termine werden auto-angepasst, bereits
 *                          zugewiesene Schichten gehen zurueck auf Entwurf.
 *   • `edit-requirement` — Einen EINZELNEN Termin bearbeiten. Wirkt
 *                          nur auf diesen einen Tag und loest ihn vom
 *                          Muster (kuenftige Muster-Aenderungen ueberschreiben
 *                          ihn nicht mehr). Wiederholung entfaellt komplett.
 *
 * Umsetzung ohne Schritt-Duplikate (Schrittzaehler bleibt sauber):
 *   - `title`/`content`/`audioStepId` sind FUNKTIONEN, die je Modus den
 *     passenden i18n-Key bzw. die passende Audiospur liefern (Muster wie bei
 *     der adaptiven Schicht-Tour, vgl. tour-step.model.ts). Basis-Variante =
 *     `create`; `edit-template` haengt `_TEMPLATE`/`-template` an,
 *     `edit-requirement` `_REQUIREMENT`/`-requirement`.
 *   - Steps, deren UI in einem Modus gar nicht existiert, entfallen via
 *     `skipIf`: das Preset-Grid gibt es nur beim Anlegen, der
 *     Wiederholungs-Block nicht beim Einzeltermin.
 *   - `renumberVisibleSteps: true` sorgt dafuer, dass der Zaehler nur die
 *     sichtbaren Steps zaehlt (z. B. „1/5 … 5/5" beim Einzelbedarf), statt
 *     Luecken zu zeigen.
 *
 * Schritt-Navigation per `wizardBridge` (`onboarding/services/wizard-bridge.ts`):
 * `onBeforeShow` ruft `goToStep(...)` (umgeht im Tour-Kontext die
 * `canNavigateToStep`-Validierung), plus `skipPresets()`,
 * `prefillBasicsForTour()`, `enableRecurring()`, damit die erklaerten Bereiche
 * im DOM sichtbar sind. Alle Anchors existieren bereits im Wizard-Template.
 *
 * Fachliche Wahrheiten (Backend `AddStaffingTemplateCommandHandler`,
 * `UpdateStaffingTemplateCommandHandler`, `UpdateStaffingRequirementCommandHandler`):
 *   - Muster = wiederkehrende Regel, NICHT eine einzelne Schicht.
 *   - Muster-Edit synchronisiert kuenftige, NICHT manuell geaenderte Termine;
 *     betroffene zugewiesene Schichten werden auf Entwurf zurueckgesetzt.
 *   - Einzeltermin-Edit setzt `IsManuallyModified=true` → der Tag wird zur
 *     Ausnahme und von kuenftigen Muster-Edits nicht mehr ueberschrieben.
 *   - Ende < Start im Zeitfenster ⇒ (Nachtschicht-Aussage bewusst weggelassen,
 *     da im Code aktuell nicht sauber abgebildet).
 */

// ───────────────────────────────────────────────────────────────────────
// Modus-Helfer: liefern je nach `wizardBridge.getMode()` den passenden
// i18n-Key bzw. die Audiospur. `available` markiert, fuer welche Edit-Modi
// eine eigene Variante existiert — fehlt sie, faellt der Helfer auf die
// Basis-(Anlegen-)Fassung zurueck.
// ───────────────────────────────────────────────────────────────────────
interface VariantAvailability {
  template?: boolean;
  requirement?: boolean;
}

/** i18n-Key mit Modus-Suffix (`_TEMPLATE` / `_REQUIREMENT`) oder Basis. */
function variantKey(
  prefix: string,
  leaf: 'TITLE' | 'CONTENT',
  available: VariantAvailability,
): string {
  const mode = wizardBridge.getMode();
  if (mode === 'edit-template' && available.template) return `${prefix}.${leaf}_TEMPLATE`;
  if (mode === 'edit-requirement' && available.requirement) return `${prefix}.${leaf}_REQUIREMENT`;
  return `${prefix}.${leaf}`;
}

/** Voice-Over-Step-ID mit Modus-Suffix (`-template` / `-requirement`) oder Basis. */
function variantAudio(base: string, available: VariantAvailability): string {
  const mode = wizardBridge.getMode();
  if (mode === 'edit-template' && available.template) return `${base}-template`;
  if (mode === 'edit-requirement' && available.requirement) return `${base}-requirement`;
  return base;
}

const I18N = 'TUTORIAL.STAFFING_WIZARD';

export const STAFFING_WIZARD_HELP_TOUR: TourConfig = {
  id: TourFeature.StaffingWizardHelp,
  version: 2,
  title: 'STAFFING_WIZARD_HELP_TOUR_TITLE',
  description: 'STAFFING_WIZARD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Planung (▦-Muster-Modus) navigieren. Der Wizard hat
  // keinen deterministischen 1-Klick-Opener (oeffnet via "＋ Neues Muster" /
  // Kalender). Daher `route` only; der Hilfe-Button lebt IM Wizard und ist
  // der eigentliche Einstieg.
  launchContext: {
    // D-12 (E1): Personalbedarf → ▦-Muster-Modus der Planung.
    // ensureLaunchContext navigiert via navigateByUrl → Query-String funktioniert.
    route: '/planner?view=muster',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 120,
  category: 'feature-discovery',
  // Modus-Steps entfallen je nach Kontext (Preset/Wiederholung) — Zaehler
  // soll nur die sichtbaren Steps zaehlen, keine Luecken zeigen.
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'STAFFING_WIZARD_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert das Grundkonzept des jeweiligen Modus — Spotlight
          // auf den ganzen Wizard, Popover daneben.
          anchor: AnchorId.WizardContainer,
          title: () => variantKey(`${I18N}.WELCOME`, 'TITLE', { template: true, requirement: true }),
          content: () => variantKey(`${I18N}.WELCOME`, 'CONTENT', { template: true, requirement: true }),
          audioStepId: () => variantAudio('welcome', { template: true, requirement: true }),
          placement: 'auto',
          dialogContext: {
            attachToDialog: true,
            autoCloseOnDialogClose: true,
            outsideDialog: true,
          },
          nextCondition: 'manual',
          onBeforeShow: () => wizardBridge.goToStep(1),
        },
        {
          id: 'preset',
          // Schritt 1: Schnellstart ODER manuell einrichten.
          // Nur beim Anlegen — im Edit-Modus ist das Preset-Grid ausgeblendet.
          anchor: AnchorId.WizardPresetGrid,
          additionalSpotlightAnchors: [AnchorId.WizardPresetSkipButton],
          title: `${I18N}.PRESET.TITLE`,
          content: `${I18N}.PRESET.CONTENT`,
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () => wizardBridge.getMode() !== 'create',
          onBeforeShow: () => wizardBridge.goToStep(1),
        },
        {
          id: 'basics',
          // Name + Abteilung: die Abteilung steuert die waehlbaren Skills.
          // skipPresets() blendet das Preset-Grid aus und zeigt das Formular.
          anchor: AnchorId.WizardBasicsSection,
          additionalSpotlightAnchors: [
            AnchorId.WizardBasicsName,
            AnchorId.WizardBasicsDepartment,
          ],
          title: `${I18N}.BASICS.TITLE`,
          content: () => variantKey(`${I18N}.BASICS`, 'CONTENT', { template: true, requirement: true }),
          audioStepId: () => variantAudio('basics', { template: true, requirement: true }),
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => {
            wizardBridge.goToStep(1);
            wizardBridge.skipPresets();
          },
        },
        {
          id: 'time',
          // Schritt 2: Zeitfenster. prefillBasicsForTour fuellt Name+Abteilung,
          // damit spaeter die Zusammenfassung nicht leer ist.
          anchor: AnchorId.WizardTimeBlock,
          title: `${I18N}.TIME.TITLE`,
          content: () => variantKey(`${I18N}.TIME`, 'CONTENT', { template: true, requirement: true }),
          audioStepId: () => variantAudio('time', { template: true, requirement: true }),
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => {
            wizardBridge.prefillBasicsForTour();
            wizardBridge.goToStep(2);
          },
        },
        {
          id: 'recurrence',
          // Schritt 2: Wiederholungsmuster. enableRecurring schaltet den Toggle
          // an, damit Pattern-Buttons/Wochentage im DOM sind. Entfaellt beim
          // Einzeltermin — dort gibt es keine Wiederholung (vom Muster
          // gesteuert).
          anchor: AnchorId.WizardRecurrenceBlock,
          additionalSpotlightAnchors: [AnchorId.WizardPatternButtons],
          title: `${I18N}.RECURRENCE.TITLE`,
          content: () => variantKey(`${I18N}.RECURRENCE`, 'CONTENT', { template: true }),
          audioStepId: () => variantAudio('recurrence', { template: true }),
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          skipIf: () => wizardBridge.getMode() === 'edit-requirement',
          onBeforeShow: () => {
            wizardBridge.goToStep(2);
            wizardBridge.enableRecurring();
          },
        },
        {
          id: 'staff',
          // Schritt 3: Personalbedarf je Qualifikation (Min/Optimal/Max).
          anchor: AnchorId.WizardStaffBlock,
          title: `${I18N}.STAFF.TITLE`,
          content: () => variantKey(`${I18N}.STAFF`, 'CONTENT', { template: true, requirement: true }),
          audioStepId: () => variantAudio('staff', { template: true, requirement: true }),
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => wizardBridge.goToStep(3),
        },
        {
          id: 'summary',
          // Schritt 4: Zusammenfassung + Speichern. Der wichtigste Modus-
          // Differenzierer: was bewirkt das Speichern konkret?
          anchor: AnchorId.WizardSummaryBlock,
          title: () => variantKey(`${I18N}.SUMMARY`, 'TITLE', { template: true, requirement: true }),
          content: () => variantKey(`${I18N}.SUMMARY`, 'CONTENT', { template: true, requirement: true }),
          audioStepId: () => variantAudio('summary', { template: true, requirement: true }),
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => wizardBridge.goToStep(4),
          // Letzter Step — kein Zurueck-Button.
          showPrevButton: false,
        },
      ],
    },
  ],
};
